export type AiCaptainStreamType =
  | 'connected'
  | 'token'
  | 'item_popover'
  | 'suggestions'
  | 'table_suggestions'
  | 'deal_suggestions'
  | 'table_status'
  | 'order_placed'
  | 'action_result'
  | 'confirm_required'
  | 'choice_prompt'
  | 'assistant_message'
  | 'error'
  | 'done';

export interface AiCaptainStreamMessage {
  type: AiCaptainStreamType;
  delta?: string;
  text?: string;
  /** Backend-authored TTS script (separate from on-screen display text). */
  speakText?: string;
  message?: string;
  item?: Record<string, unknown>;
  items?: Record<string, unknown>[];
  tables?: Record<string, unknown>[];
  deals?: Record<string, unknown>[];
  table?: Record<string, unknown>;
  order?: Record<string, unknown>;
  action?: string;
  ok?: boolean;
  cart?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  choice?: Record<string, unknown>;
  hasMore?: boolean;
}

export interface AiCaptainClientOptions {
  wsUrl: string;
  onMessage: (msg: AiCaptainStreamMessage) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export interface AiCaptainSendPayload {
  type: 'user_message' | 'page_event';
  restaurantId: string;
  appName: string;
  customerId: string;
  userId: number;
  tableId?: string | null;
  orderId?: number | null;
  page: string;
  context?: Record<string, unknown>;
  text?: string;
  authToken?: string;
}

const MAX_FLUSH_ATTEMPTS = 60;
const FLUSH_INTERVAL_MS = 50;
const RECONNECT_DELAY_MS = 5000;

function readAuthTokenFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)authToken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

export class AiCaptainClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private pendingQueue: AiCaptainSendPayload[] = [];
  private flushAttempts = 0;

  constructor(private options: AiCaptainClientOptions) {}

  connect(): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.intentionalClose = false;
    this.ws = new WebSocket(this.options.wsUrl);

    this.ws.onopen = () => {
      this.flushAttempts = 0;
      this.options.onConnectionChange?.(true);
      this.flushQueue();
    };

    this.ws.onclose = () => {
      this.ws = null;
      this.options.onConnectionChange?.(false);
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      this.options.onConnectionChange?.(false);
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as AiCaptainStreamMessage;
        if (msg.type === 'connected') {
          this.options.onConnectionChange?.(true);
          return;
        }
        this.options.onMessage(msg);
      } catch {
        // ignore malformed frames
      }
    };
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.pendingQueue = [];
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.options.onConnectionChange?.(false);
  }

  send(payload: AiCaptainSendPayload): void {
    this.pendingQueue.push({
      ...payload,
      authToken: payload.authToken ?? readAuthTokenFromCookie(),
    });
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushAttempts = 0;
    this.flushTimer = setTimeout(() => this.flushQueue(), 0);
  }

  private flushQueue(): void {
    this.flushTimer = null;

    if (this.intentionalClose) return;

    if (this.ws?.readyState === WebSocket.OPEN) {
      while (this.pendingQueue.length > 0) {
        const next = this.pendingQueue.shift();
        if (next) {
          this.ws.send(JSON.stringify(next));
        }
      }
      this.flushAttempts = 0;
      return;
    }

    this.flushAttempts += 1;
    if (this.flushAttempts >= MAX_FLUSH_ATTEMPTS) {
      this.flushAttempts = 0;
      return;
    }

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    }

    this.flushTimer = setTimeout(() => this.flushQueue(), FLUSH_INTERVAL_MS);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.intentionalClose) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.pendingQueue.length > 0 || !this.intentionalClose) {
        this.connect();
      }
    }, RECONNECT_DELAY_MS);
  }
}

export function isLocalOrchestratorHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || /\.localhost$/i.test(hostname);
}

export function getAiCaptainWsUrl(): string {
  const fromEnv = process.env.REACT_APP_AI_CAPTAIN_WS_URL;
  if (fromEnv) return fromEnv;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = isLocalOrchestratorHost(window.location.hostname)
    ? `${window.location.hostname}:8090`
    : window.location.host;
  return `${protocol}//${host}/captain/stream`;
}
